import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download, FileText, BarChart3, LineChart, PieChart, Smartphone, Calculator, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';

const MutualFundsPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      document.title = 'Mutual Fund Investment Advisory in Jaipur | SIP, ELSS, NRI | APK Fintech';
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = 'Start or grow your mutual fund investments with APK Fintech \u2014 a CA-backed, AMFI-registered advisor in Jaipur. Goal-based SIPs, ELSS, NRI mutual funds, and real-time portfolio tracking on the APK Fintech app.';
    }, 100);
    return () => {
      clearTimeout(timer);
      document.title = 'APKfintech';
    };
  }, []);

  return (
    <div className="category-page" data-testid="mutual-funds-page">
      <div className="category-page-container">

        {/* Breadcrumb */}
        <div className="getstarted-breadcrumb">
          <Link to="/" className="breadcrumb-link">Home</Link>
          <span className="breadcrumb-sep">/</span>
          <Link to="/services" className="breadcrumb-link">Services</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">Mutual Funds</span>
        </div>

        <h1 className="category-page-title" data-testid="mf-page-title">Mutual Funds</h1>

        {/* Hero Section */}
        <div className="mf-hero" data-testid="mf-hero">
          <p className="mf-hero-tagline">The Right Fund. At the Right Time. For the Right Goal.</p>
          <h2 className="mf-hero-headline">India's Most Accessible Wealth-Building Instrument — Personalised for You.</h2>
          <div className="mf-hero-description">
            <p>Mutual Funds remain India's most accessible, tax-efficient, and diversified wealth-building instrument. At APK Fintech, we go beyond simply recommending funds — we architect a personalised mutual fund portfolio that reflects your risk appetite, income, tax slab, and life goals.</p>
            <p>We are AMFI-registered Mutual Fund Distributors (MFDs) with a CA background, which means every fund we recommend is evaluated not just for performance but for its full tax impact on your specific situation.</p>
          </div>
          <div className="mf-hero-buttons">
            <Link to="/contact">
              <Button className="btn-primary mf-hero-btn-primary" data-testid="mf-sip-cta">
                Start Your SIP Today <ArrowRight size={20} />
              </Button>
            </Link>
            <a href="https://apkfintech.my-portfolio.co.in/app/#/login" target="_blank" rel="noopener noreferrer">
              <Button className="mf-hero-btn-secondary" data-testid="mf-app-cta">
                <Download size={18} /> Download the APK Fintech App
              </Button>
            </a>
          </div>
          <div className="mf-hero-stats" data-testid="mf-hero-stats">
            <div className="mf-hero-stat">
              <span className="mf-stat-value">40+ AMCs</span>
              <span className="mf-stat-label">All major fund houses accessible in one place</span>
            </div>
            <div className="mf-hero-stat">
              <span className="mf-stat-value">1-Click Statements</span>
              <span className="mf-stat-label">Download any AMC statement instantly from the app</span>
            </div>
            <div className="mf-hero-stat">
              <span className="mf-stat-value">CA-Led Advisory</span>
              <span className="mf-stat-label">Every fund recommended through a tax and returns lens</span>
            </div>
            <div className="mf-hero-stat">
              <span className="mf-stat-value">AMFI Registered</span>
              <span className="mf-stat-label">Regulated, transparent, conflict-free distribution</span>
            </div>
          </div>
        </div>

        {/* What Sets Us Apart */}
        <div className="mf-apart-section" data-testid="mf-apart">
          <h2 className="mf-apart-heading">What Sets APK Fintech Apart in Mutual Fund Advisory</h2>
          <p className="mf-apart-intro">Any distributor can place a mutual fund order. What we do is fundamentally different — we build, manage, and continuously optimise a mutual fund portfolio that grows with your life.</p>
          <div className="mf-apart-grid">
            <div className="mf-apart-card" data-testid="mf-apart-0">
              <h4 className="mf-apart-card-title">Goal-Based Fund Selection</h4>
              <p className="mf-apart-card-desc">We link every SIP recommendation to a specific financial goal — retirement, child education, home purchase, or wealth creation. You always know which fund is working for which goal, and why.</p>
            </div>
            <div className="mf-apart-card" data-testid="mf-apart-1">
              <h4 className="mf-apart-card-title">Tax-Optimised Structuring</h4>
              <p className="mf-apart-card-desc">We plan your mutual fund portfolio around your tax slab. ELSS for Section 80C savings, debt fund timing for indexation, and capital gains harvesting — your post-tax returns are always the priority.</p>
            </div>
            <div className="mf-apart-card" data-testid="mf-apart-2">
              <h4 className="mf-apart-card-title">Advanced Capital Gain Reporting</h4>
              <p className="mf-apart-card-desc">FY-wise realised and unrealised capital gain reports — available at one click through the APK Fintech app. No scrambling at tax time.</p>
            </div>
            <div className="mf-apart-card" data-testid="mf-apart-3">
              <h4 className="mf-apart-card-title">SIP & STP Management</h4>
              <p className="mf-apart-card-desc">Active tracking of all running, upcoming, and lapsed SIPs and STPs. We alert you before a mandate expires or an SIP fails — no investment slips through the cracks.</p>
            </div>
            <div className="mf-apart-card" data-testid="mf-apart-4">
              <h4 className="mf-apart-card-title">NFO Access & Alerts</h4>
              <p className="mf-apart-card-desc">Timely alerts on new fund offers (NFOs) that match your portfolio strategy, with seamless online investment as they open.</p>
            </div>
            <div className="mf-apart-card" data-testid="mf-apart-5">
              <h4 className="mf-apart-card-title">1-Click AMC Statements</h4>
              <p className="mf-apart-card-desc">Consolidated account statements from 40+ AMCs downloadable instantly from the app — for any period, any fund, any time.</p>
            </div>
          </div>
          <div className="mf-apart-cta">
            <Link to="/contact">
              <Button className="btn-primary mf-hero-btn-primary" data-testid="mf-apart-cta-btn">
                Talk to Our Mutual Fund Advisor <ArrowRight size={20} />
              </Button>
            </Link>
          </div>
        </div>

        {/* Categories We Cover */}
        <div className="mf-categories-section" data-testid="mf-categories">
          <h2 className="mf-apart-heading">Mutual Fund Categories We Cover</h2>
          <p className="mf-apart-intro">From your first SIP to a sophisticated multi-fund portfolio — we advise across every mutual fund category available in India.</p>
          <div className="mf-apart-grid">
            <div className="mf-apart-card" data-testid="mf-cat-0">
              <h4 className="mf-apart-card-title">SIP, STP & SWP</h4>
              <p className="mf-apart-card-desc">Systematic Investment Plans, Transfer Plans, and Withdrawal Plans — set up, tracked, and managed for you. We handle mandate renewals and alert you before any SIP is at risk of failing.</p>
            </div>
            <div className="mf-apart-card" data-testid="mf-cat-1">
              <h4 className="mf-apart-card-title">Lumpsum Investments</h4>
              <p className="mf-apart-card-desc">One-time investments placed at the right time, in the right fund, with full transaction tracking and order confirmation through the app.</p>
            </div>
            <div className="mf-apart-card" data-testid="mf-cat-2">
              <h4 className="mf-apart-card-title">ELSS & Tax Saving Funds</h4>
              <p className="mf-apart-card-desc">Equity Linked Saving Schemes with a 3-year lock-in that qualify for Section 80C deduction up to &#8377;1.5 Lakhs. The most tax-efficient way to save — with the best long-term return potential.</p>
            </div>
            <div className="mf-apart-card" data-testid="mf-cat-3">
              <h4 className="mf-apart-card-title">Debt Funds & Liquid Funds</h4>
              <p className="mf-apart-card-desc">For capital preservation, regular income, and parking surplus cash. We recommend the right debt fund based on your investment horizon and tax bracket — not just the highest yield.</p>
            </div>
            <div className="mf-apart-card" data-testid="mf-cat-4">
              <h4 className="mf-apart-card-title">Index Funds & ETFs</h4>
              <p className="mf-apart-card-desc">Low-cost, passive investing that tracks benchmark indices — Nifty 50, Sensex, Nifty Next 50, and sector ETFs. Ideal for building a disciplined, cost-efficient core portfolio.</p>
            </div>
            <div className="mf-apart-card" data-testid="mf-cat-5">
              <h4 className="mf-apart-card-title">International Funds</h4>
              <p className="mf-apart-card-desc">Mutual funds that invest in global equities — US tech, global diversified, and international thematic funds. Diversify your portfolio beyond India, in rupees.</p>
            </div>
          </div>
          <div className="mf-soft-cta" data-testid="mf-soft-cta">
            <p className="mf-soft-cta-text">Not sure which category is right for you? We'll help you decide.</p>
            <Link to="/contact" className="mf-soft-cta-link" data-testid="mf-speak-advisor">
              Speak to an Advisor <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* How We Build Your Portfolio - Timeline */}
        <div className="inh-process-section" data-testid="mf-process">
          <h2 className="inh-process-heading">How We Build Your Mutual Fund Portfolio</h2>
          <p className="mf-apart-intro">A fund recommendation without a process behind it is just a guess. Here is exactly how APK Fintech builds and manages your mutual fund portfolio.</p>
          <div className="inh-process-timeline">
            {[
              { title: 'Discovery Call', body: 'We understand your financial situation, income, existing investments, tax slab, risk profile, and life goals — through a structured consultation. No obligation, no sales pitch.' },
              { title: 'Financial Health Check', body: 'We audit your current portfolio — including any existing mutual funds, FDs, insurance policies, and other investments — to identify gaps, overlaps, and tax inefficiencies.' },
              { title: 'Personalised Fund Plan', body: 'We build a custom asset allocation — across equity, debt, hybrid, and international funds — with each fund linked to a specific goal, time horizon, and expected return. We explain every recommendation in plain language before you invest a single rupee.' },
              { title: 'Seamless Execution & KYC', body: 'We handle all onboarding paperwork — KYC, SIP mandate setup, bank account linking, and folio creation. You review, approve, and we execute.' },
              { title: 'Ongoing Review & Rebalancing', body: 'Quarterly portfolio reviews, annual plan updates, and proactive rebalancing when market conditions or your life goals change. You always know where your portfolio stands — in real time, on the APK Fintech app.' },
            ].map((step, idx, arr) => (
              <div key={idx} className="inh-process-step" data-testid={`mf-step-${idx}`}>
                <div className="inh-step-marker">
                  <span className="inh-step-number">{idx + 1}</span>
                  {idx < arr.length - 1 && <div className="inh-step-line" />}
                </div>
                <div className="inh-step-content">
                  <h4 className="inh-step-title">Step {idx + 1} — {step.title}</h4>
                  <p className="inh-step-body">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Who Should Invest */}
        <div className="inh-serve-section" data-testid="mf-who-invest">
          <h2 className="inh-serve-heading">Who Should Invest in Mutual Funds?</h2>
          <p className="mf-apart-intro">Mutual funds are for everyone — but the right fund is not the same for everyone. Here is how we serve investors at every stage of life.</p>
          <div className="inh-serve-grid">
            <div className="inh-serve-card" data-testid="mf-segment-0">
              <h4 className="inh-serve-card-title">First-Time Investor</h4>
              <p className="inh-serve-card-body">You want to start investing but do not know where to begin. We start with a &#8377;500 SIP in a single diversified fund, explain every step, and scale as your confidence grows.</p>
              <p className="inh-serve-card-services"><span>Best for:</span> SIP starter plan, ELSS for 80C, emergency fund in liquid fund.</p>
            </div>
            <div className="inh-serve-card" data-testid="mf-segment-1">
              <h4 className="inh-serve-card-title">Salaried Professional</h4>
              <p className="inh-serve-card-body">You earn well but want your savings to grow faster than inflation. We build a goal-based SIP portfolio — retirement, home, education — and optimise it for your tax slab every year.</p>
              <p className="inh-serve-card-services"><span>Best for:</span> Goal SIPs, ELSS, debt fund for liquidity, tax-harvesting.</p>
            </div>
            <div className="inh-serve-card" data-testid="mf-segment-2">
              <h4 className="inh-serve-card-title">Business Owner</h4>
              <p className="inh-serve-card-body">Your income is irregular and surplus cash needs to work harder. We use liquid funds and systematic transfer plans to deploy cash efficiently, without locking it away.</p>
              <p className="inh-serve-card-services"><span>Best for:</span> Liquid funds, STPs, debt funds, ELSS for Section 80C.</p>
            </div>
            <div className="inh-serve-card" data-testid="mf-segment-3">
              <h4 className="inh-serve-card-title">HNI / High-Net-Worth Investor</h4>
              <p className="inh-serve-card-body">You want more than standard mutual fund returns. We layer index funds, international funds, and debt funds strategically alongside your PMS and AIF portfolio.</p>
              <p className="inh-serve-card-services"><span>Best for:</span> Index funds, international diversification, debt optimisation.</p>
            </div>
            <div className="inh-serve-card" data-testid="mf-segment-4">
              <h4 className="inh-serve-card-title">NRI Investor</h4>
              <p className="inh-serve-card-body">You want to invest in India's growth from abroad — simply and compliantly. We manage NRE/NRO-linked SIPs and lumpsum investments with full FEMA compliance and capital gain reporting.</p>
              <p className="inh-serve-card-services"><span>Best for:</span> NRE/NRO-linked SIPs, ELSS, diversified equity funds.</p>
            </div>
            <div className="inh-serve-card" data-testid="mf-segment-5">
              <h4 className="inh-serve-card-title">Retiree / Senior Citizen</h4>
              <p className="inh-serve-card-body">You want regular income and capital preservation. We recommend the right mix of debt funds, balanced advantage funds, and SWP (Systematic Withdrawal Plans) to generate monthly income without depleting capital.</p>
              <p className="inh-serve-card-services"><span>Best for:</span> Debt funds, SWP, balanced advantage funds, liquid funds.</p>
            </div>
          </div>
        </div>

        {/* App Features Banner */}
        <div className="mf-app-banner" data-testid="mf-app-banner">
          <h2 className="mf-app-banner-heading">Your Entire Mutual Fund Portfolio — On One Screen</h2>
          <p className="mf-app-banner-subtext">The APK Fintech app gives you a real-time, consolidated view of every mutual fund investment — across all AMCs, all folios, all goals — in a single dashboard. Available on iOS and Android.</p>
          <div className="mf-app-features">
            <div className="mf-app-feature" data-testid="mf-app-feature-0">
              <div className="mf-app-feature-icon"><FileText size={22} /></div>
              <div><strong>Complete Portfolio Report</strong><p>Download a full portfolio report across all asset classes in one tap.</p></div>
            </div>
            <div className="mf-app-feature" data-testid="mf-app-feature-1">
              <div className="mf-app-feature-icon"><BarChart3 size={22} /></div>
              <div><strong>1-Click AMC Statements</strong><p>Instant account statements from any of 40+ AMCs, for any period.</p></div>
            </div>
            <div className="mf-app-feature" data-testid="mf-app-feature-2">
              <div className="mf-app-feature-icon"><LineChart size={22} /></div>
              <div><strong>Capital Gain Reports</strong><p>FY-wise realised and unrealised capital gain reports — formatted for ITR filing.</p></div>
            </div>
            <div className="mf-app-feature" data-testid="mf-app-feature-3">
              <div className="mf-app-feature-icon"><PieChart size={22} /></div>
              <div><strong>SIP & STP Dashboard</strong><p>View all running, upcoming, and lapsed SIPs and STPs in one screen.</p></div>
            </div>
            <div className="mf-app-feature" data-testid="mf-app-feature-4">
              <div className="mf-app-feature-icon"><Smartphone size={22} /></div>
              <div><strong>Online Investments</strong><p>Invest in any mutual fund scheme or NFO directly from the app.</p></div>
            </div>
            <div className="mf-app-feature" data-testid="mf-app-feature-5">
              <div className="mf-app-feature-icon"><Calculator size={22} /></div>
              <div><strong>Financial Calculators</strong><p>SIP calculator, lumpsum calculator, goal planner, and compounding calculator.</p></div>
            </div>
          </div>
          <div className="mf-app-download-buttons">
            <a href="https://apkfintech.my-portfolio.co.in/app/#/login" target="_blank" rel="noopener noreferrer">
              <Button className="mf-app-dl-btn" data-testid="mf-ios-btn">
                <Download size={18} /> Download on App Store
              </Button>
            </a>
            <a href="https://apkfintech.my-portfolio.co.in/app/#/login" target="_blank" rel="noopener noreferrer">
              <Button className="mf-app-dl-btn" data-testid="mf-android-btn">
                <Download size={18} /> Get it on Google Play
              </Button>
            </a>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="inh-faq-section" data-testid="mf-faq">
          <h2 className="inh-faq-heading">Frequently Asked Questions</h2>
          <div className="inh-faq-list">
            {[
              { q: 'What is the minimum amount to start investing in mutual funds?', a: 'You can start with as little as \u20B9100\u2013\u20B9500 per month through an SIP, depending on the fund. There is no maximum limit. For lumpsum investments, most funds have a minimum of \u20B91,000. APK Fintech helps you start with the right amount for your situation \u2014 not just the minimum.' },
              { q: 'Are mutual funds safe?', a: 'Mutual funds are market-linked investments \u2014 returns are not guaranteed and are subject to market risk. However, they are regulated by SEBI and AMFI, which ensures transparency, daily NAV disclosure, and investor protection. The right fund for the right goal and time horizon significantly reduces risk. We guide you through this.' },
              { q: 'What is the difference between SIP and lumpsum investment?', a: 'An SIP (Systematic Investment Plan) invests a fixed amount every month, averaging out your purchase cost over time \u2014 ideal for salaried investors. A lumpsum is a one-time investment of a larger amount \u2014 better suited when you have a surplus and markets are at a reasonable level. We advise on which approach suits your situation.' },
              { q: 'How is APK Fintech different from investing directly with an AMC?', a: 'When you invest directly, you manage everything yourself \u2014 fund selection, rebalancing, tax planning, documentation, and review. With APK Fintech, you get a CA-backed advisor who recommends funds based on your goals and tax slab, tracks your portfolio, alerts you when action is needed, and provides comprehensive reporting \u2014 all in one place.' },
              { q: 'Are mutual fund returns taxable in India?', a: 'Yes. Equity mutual funds held for more than 1 year attract Long-Term Capital Gains (LTCG) tax at 12.5% on gains above \u20B91.25 Lakhs per year. Short-term gains (held under 1 year) are taxed at 20%. Debt fund gains are taxed as per your income tax slab. Our CA team plans your portfolio to minimise tax impact legally.' },
              { q: 'Can NRIs invest in mutual funds in India?', a: 'Yes. NRIs can invest in most Indian mutual funds through their NRE or NRO accounts, subject to FEMA regulations. Some AMCs restrict US and Canadian NRIs due to FATCA compliance requirements. APK Fintech handles all NRI onboarding, KYC, and compliance.' },
              { q: 'How do I track my mutual fund portfolio with APK Fintech?', a: 'Through the APK Fintech app, you can view your complete portfolio in real time \u2014 across all AMCs and folios. The app shows current value, returns, capital gains, SIP status, and allows one-click statement downloads. Available free on iOS and Android.' },
            ].map((item, idx) => (
              <div
                key={idx}
                className={`inh-faq-item ${openFaq === idx ? 'inh-faq-open' : ''}`}
                data-testid={`mf-faq-item-${idx}`}
              >
                <button
                  className="inh-faq-question"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  data-testid={`mf-faq-toggle-${idx}`}
                >
                  <span>{item.q}</span>
                  <ChevronDown size={20} className="inh-faq-chevron" />
                </button>
                {openFaq === idx && (
                  <div className="inh-faq-answer" data-testid={`mf-faq-answer-${idx}`}>
                    <p>{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Final CTA — Full Width */}
      <div className="mf-final-cta" data-testid="mf-final-cta">
        <div className="mf-final-cta-inner">
          <h2 className="mf-final-cta-heading">Ready to Start Your Mutual Fund Journey?</h2>
          <p className="mf-final-cta-subtext">Whether it is your first SIP or a complete portfolio review — we are here to help. Book a free 30-minute consultation with our CA-backed mutual fund advisor. No commitment. Just clarity.</p>
          <div className="mf-final-cta-buttons">
            <Link to="/contact">
              <Button className="btn-primary mf-final-btn-primary" data-testid="mf-cta-consult">
                Book a Free 30-Min Consultation <ArrowRight size={20} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="mf-final-btn-secondary" data-testid="mf-cta-review">
                Get a Free Portfolio Review
              </Button>
            </Link>
            <a href="https://apkfintech.my-portfolio.co.in/app/#/login" target="_blank" rel="noopener noreferrer" className="mf-final-soft-link" data-testid="mf-cta-app">
              <Download size={16} /> Download the APK Fintech App
            </a>
          </div>
          <p className="mf-final-trust-line">AMFI Registered &middot; CA-Led Advisory &middot; SEBI Compliant &middot; Conflict-Free</p>
        </div>
      </div>
    </div>
  );
};

export default MutualFundsPage;
